// Shared markdown utilities for Arnold (AI Coach)
// Keep this small and predictable: sanitize only what breaks rendering.

export const sanitizeMarkdown = (text: string): string => {
  let sanitized = text;

  // 0) Strip thematic-break / truncation artifacts the model sometimes emits mid-prose.
  sanitized = sanitized.replace(/\*{3,}/g, " ");
  sanitized = sanitized.replace(/_{3,}/g, " ");

  // 0a) Break inline headings onto their own line.
  // Models sometimes emit "...end of sentence. ### What's Next\n\nNext paragraph..."
  // where the ###/## sits mid-line and renders as literal "### What's Next".
  // Force any ##/###/#### that is NOT at the start of a line to start a new block.
  sanitized = sanitized.replace(/([^\n])[ \t]+(#{2,4})\s+/g, "$1\n\n$2 ");

  // 0b) Fix heading syntax: convert single # to ## (Arnold is instructed to use ##)
  sanitized = sanitized.replace(/^#\s+(?!#)/gm, "## ");
  sanitized = sanitized.replace(/(\n)#\s+(?!#)/g, "$1## ");

  // Unescape headings that were incorrectly escaped (\#)
  sanitized = sanitized.replace(/^\#\s+/gm, "## ");
  sanitized = sanitized.replace(/(\n)\#\s+/g, "$1## ");

  // 1) Close unclosed bold tags (**)
  const boldMatches = sanitized.match(/\*\*/g);
  if (boldMatches && boldMatches.length % 2 !== 0) {
    const lastIndex = sanitized.lastIndexOf("**");
    if (lastIndex !== -1) {
      sanitized = sanitized.substring(0, lastIndex) + sanitized.substring(lastIndex + 2);
    }
  }

  // 2) Close unclosed italic tags (*) - conservative; avoid breaking lists
  const singleAsterisks = (sanitized.match(/(?<!\*)\*(?!\*)/g) || []).length;
  if (singleAsterisks % 2 !== 0) {
    sanitized = sanitized.replace(/(?<!\*)\*(?!\*)(?![*-])(?![^\n]*$)/g, "");
  }

  // 3) Close unclosed underline tags (__)
  const underlineMatches = sanitized.match(/__/g);
  if (underlineMatches && underlineMatches.length % 2 !== 0) {
    const lastIndex = sanitized.lastIndexOf("__");
    if (lastIndex !== -1) {
      sanitized = sanitized.substring(0, lastIndex) + sanitized.substring(lastIndex + 2);
    }
  }

  // 4) Normalize bullets: convert * bullets to - bullets (avoid **bold**)
  sanitized = sanitized.replace(/^\s*\*\s+(?!\*)/gm, "- ");
  sanitized = sanitized.replace(/([^\n])\s+\*\s+(?!\*)/g, "$1 - ");

  // 5) Normalize paragraph spacing
  sanitized = sanitized.replace(/\n{3,}/g, "\n\n");

  return sanitized;
};

/**
 * Converts inline dash patterns to proper list items on separate lines
 * Handles patterns like: "text. - **Item:** description - **Item2:** description"
 */
const convertInlineDashesToList = (text: string): string => {
  // Split inline dash-delimited list items onto separate lines
  // Pattern: ". - **Something" or "! - **Something" or "? - **Something"
  let result = text.replace(/([.!?])\s+-\s+(\*\*)/g, "$1\n\n- $2");
  
  // Also handle cases where there's text before the first dash item
  // "Here's a workout: - **Exercise 1:**" -> "Here's a workout:\n\n- **Exercise 1:**"
  result = result.replace(/:\s+-\s+(\*\*)/g, ":\n\n- $1");
  
  return result;
};

/** Title glued to "Name, rest of sentence" — e.g. "VO2 Max Update Elis, your latest..." */
const splitTitleFromNameGreeting = (content: string): { title: string; body: string } | null => {
  // Prefer: short TitleCase/metric title + FirstName + comma + lowercase continuation
  const nameComma = content.match(
    /^((?:[A-Z0-9][A-Za-z0-9+/-]{0,24}(?:\s+[A-Z0-9][A-Za-z0-9+/-]{0,24}){0,5}))\s+([A-Z][a-z]{1,20}),\s+([a-z].+)$/,
  );
  if (nameComma) {
    const title = nameComma[1].trim();
    const body = `${nameComma[2]}, ${nameComma[3].trim()}`;
    if (title.split(/\s+/).length <= 6 && title.length <= 48) {
      return { title, body };
    }
  }
  return null;
};

/**
 * Splits headings/titles that are accidentally merged with following sentences
 * Handles: "Your Next Power Session! It sounds like..." -> heading + paragraph
 * Handles: "VO2 Max Update Elis, your latest..." -> ## VO2 Max Update + body
 */
const splitMergedTitleSentences = (text: string): string => {
  const lines = text.split("\n");
  const result: string[] = [];

  // Common sentence starters that indicate a new sentence after a title
  const lowercaseStarters = /^(it|i|sounds|let's|lets|here's|heres|this|that|the|a|an|we|you|your|my|our|based|given|considering|looking|after|before|since|because|as|for|with|from|to|in|on|at|so|now|alright|okay|ok)\b/i;
  
  // Capitalized sentence starters (include first names — models glue "Title Name, ...")
  const capitalizedStarters = /^(It|I|Sounds|Let's|Lets|Here's|Heres|This|That|The|A|An|We|You|Your|My|Our|Based|Given|Considering|Looking|After|Before|Since|Because|As|For|With|From|To|In|On|At|So|Now|Alright|Okay|Ok|Remember|Great|Excellent|Nice|Solid|Good|Amazing|Keep|Today|Ready|Absolutely|Definitely|Certainly|Sure|Perfect|Awesome|Fantastic|Wonderful|[A-Z][a-z]{1,20})\b/;

  for (const line of lines) {
    // Skip empty lines
    if (!line.trim()) {
      result.push(line);
      continue;
    }

    // Check for heading lines (## Title merged with sentence)
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const [, hashes, content] = headingMatch;

      const nameSplit = splitTitleFromNameGreeting(content);
      if (nameSplit) {
        result.push(`${hashes} ${nameSplit.title}`);
        result.push("");
        result.push(nameSplit.body);
        continue;
      }
      
      // Look for pattern: "Title! sentence" or "Title? sentence" or "Title. sentence"
      const mergedMatch = content.match(/^(.{3,60}[!?.])\s+([A-Za-z].+)$/);
      if (mergedMatch) {
        const [, title, sentence] = mergedMatch;
        // Only split if the sentence part starts with a common sentence starter
        if (capitalizedStarters.test(sentence) || lowercaseStarters.test(sentence)) {
          result.push(`${hashes} ${title.trim()}`);
          result.push("");
          result.push(sentence.trim());
          continue;
        }
      }
      
      result.push(line);
      continue;
    }

    // Bold-only line that glues title + greeting: **VO2 Max Update Elis, your latest...**
    const boldOnlyGlued = line.match(/^\s*\*\*(.+?)\*\*\s*$/);
    if (boldOnlyGlued) {
      const nameSplit = splitTitleFromNameGreeting(boldOnlyGlued[1].trim());
      if (nameSplit) {
        result.push(`## ${nameSplit.title}`);
        result.push("");
        result.push(nameSplit.body);
        continue;
      }
    }

    // Check for bold titles merged with sentences
    // Pattern: "**Title Here!** It sounds like..." OR "**VO2 Max Update** Elis, your latest..."
    const boldMergedMatch = line.match(/^(\*\*[^*]+\*\*[!?.]?)\s+([A-Za-z].+)$/);
    if (boldMergedMatch) {
      const [, boldTitle, sentence] = boldMergedMatch;
      const cleanTitle = boldTitle.replace(/\*\*/g, "").replace(/[!?.]+$/g, "").trim();
      if (
        cleanTitle.split(/\s+/).length <= 8 &&
        (capitalizedStarters.test(sentence) || lowercaseStarters.test(sentence))
      ) {
        result.push(`## ${cleanTitle}`);
        result.push("");
        result.push(sentence.trim());
        continue;
      }
    }

    // Check for plain titles ending with ! or ? merged with sentences
    // Pattern: "Your Next Power Session! It sounds like..."
    const plainMergedMatch = line.match(/^([A-Z][^.!?]{2,50}[!?])\s+([A-Za-z].+)$/);
    if (plainMergedMatch) {
      const [, title, sentence] = plainMergedMatch;
      if (capitalizedStarters.test(sentence) || lowercaseStarters.test(sentence)) {
        result.push(`## ${title.trim()}`);
        result.push("");
        result.push(sentence.trim());
        continue;
      }
    }

    // Plain first line: "VO2 Max Update Elis, your latest..."
    const plainNameSplit = splitTitleFromNameGreeting(line.trim());
    if (plainNameSplit && result.every((l) => !l.trim())) {
      result.push(`## ${plainNameSplit.title}`);
      result.push("");
      result.push(plainNameSplit.body);
      continue;
    }

    result.push(line);
  }

  return result.join("\n");
};

/**
 * Splits inline bold labels onto separate lines/bullets.
 * Handles: "...text **Total Distance:** 8km **Estimated Duration:** 45 min"
 * becomes separate bullet points for each bold label.
 */
const splitInlineBoldLabels = (text: string): string => {
  const lines = text.split("\n");
  const result: string[] = [];

  for (const line of lines) {
    // Count bold label occurrences like **Label:** in the line
    const boldLabelPattern = /\*\*[A-Z][^*]{1,40}:\*\*/g;
    const matches = [...line.matchAll(boldLabelPattern)];

    // Only split if there are 2+ bold labels crammed into one line
    if (matches.length >= 2) {
      // Check if line starts with a bullet
      const bulletMatch = line.match(/^(\s*[-•]\s*)/);
      const bulletPrefix = bulletMatch ? bulletMatch[1] : "";

      // Split at each bold label boundary (except the first one)
      // Use a regex that captures right before **Label:**
      const parts = line.split(/(?=\*\*[A-Z][^*]{1,40}:\*\*)/g).filter(s => s.trim());

      for (let i = 0; i < parts.length; i++) {
        let part = parts[i].trim();
        // Remove leading pipe separators
        part = part.replace(/^\|\s*/, "").trim();
        if (!part) continue;

        if (i === 0 && bulletPrefix && part.startsWith(bulletPrefix.trim())) {
          result.push(part);
        } else {
          result.push(`${bulletPrefix || "- "}${part}`);
        }
      }
    } else {
      result.push(line);
    }
  }

  return result.join("\n");
};

export const normalizeAiMarkdownForDisplay = (text: string): string => {
  if (!text) return "";

  let out = sanitizeMarkdown(text);

  // Split inline bold labels onto separate lines
  out = splitInlineBoldLabels(out);

  // First: convert inline dashes to proper list items on new lines
  out = convertInlineDashesToList(out);

  // Ensure list bullets are consistent
  out = out.replace(/^\s*\*\s+(?!\*)/gm, "- ");

  // Split titles that are merged with following sentences
  out = splitMergedTitleSentences(out);

  // Unwrap full-line bold that is clearly a sentence (not a short title/label)
  out = out
    .split("\n")
    .map((line) => {
      const m = line.match(/^\s*\*\*([^*]+)\*\*\s*$/);
      if (!m) return line;
      const inner = m[1].trim();
      const words = inner.split(/\s+/).filter(Boolean);
      if (/:\s*$/.test(inner)) return line; // keep **Label:**
      if (words.length <= 5 && !/\b(is|are|was|were|logged|indicator)\b/i.test(inner)) {
        return line; // short title-like bold
      }
      if (words.length >= 6 || /\b(is|are|was|were|your|the|this|that|logged|indicator)\b/i.test(inner)) {
        return inner;
      }
      return line;
    })
    .join("\n");

  // Rejoin false mid-sentence paragraph breaks (e.g. "Elis, your latest" / "logged VO2...")
  out = out.replace(/\n{3,}/g, "\n\n");
  out = out.replace(/([a-z],|latest|logged|your|the|a|an|is|at|of|to|for)\n\n([a-z0-9])/gi, "$1 $2");

  // Strip dangling list artifacts like "sentence. -" at end of lines
  out = out.replace(/\s+-\s*$/gm, "");

  // Convert short bold-only TITLE lines into headings — never promote conversational sentences.
  const lines = out.split("\n");
  const transformed: string[] = [];

  const fillerPhraseRe =
    /\b(excellent|great|good|nice|solid|amazing|perfect|awesome|fantastic)\s+(question|work|job|point|stuff)\b/i;
  // Do NOT include user first names here — that demoted real titles like "VO2 Max Update".
  const greetingTokenRe =
    /\b(hey|hi|hello|excellent|great|let'?s|here'?s|sounds|absolutely|definitely)\b/i;
  const sentenceyRe =
    /\b(is|are|was|were|will|would|can|could|should|need|needs|with|your|you'?re|i'?m|we'?ll|to run|to build|for your)\b/i;

  const cleanHeadingText = (raw: string) =>
    raw
      .replace(fillerPhraseRe, "")
      .replace(/\s{2,}/g, " ")
      .replace(/[,!?.:;]+$/g, "")
      .trim();

  for (const line of lines) {
    const boldOnly = line.match(/^\s*\*\*(.+?)\*\*\s*$/);
    const headingLine = line.match(/^\s*(#{2,4})\s+(.+?)\s*$/);

    if (!boldOnly && !headingLine) {
      transformed.push(line);
      continue;
    }

    const level = headingLine ? headingLine[1] : "##";
    let innerRaw = (boldOnly ? boldOnly[1] : headingLine![2]).trim();
    if (!innerRaw) continue;

    // Split title glued to filler: "Refine 10km Race Endurance Excellent question"
    const fillerSplit = innerRaw.match(
      /^(.{3,60}?)\s+((?:Excellent|Great|Good|Nice|Solid|Amazing|Perfect|Awesome)\s+(?:question|work|job|point).*)$/i,
    );
    if (fillerSplit) {
      const title = cleanHeadingText(fillerSplit[1]);
      const rest = fillerSplit[2].trim();
      if (title && title.split(/\s+/).length <= 8) {
        transformed.push(`${level} ${title}`);
        transformed.push("");
        transformed.push(rest);
        continue;
      }
    }

    // Preserve sentence punctuation when demoting; only strip trailing punct for real titles.
    const words = innerRaw.split(/\s+/).filter(Boolean);
    const looksLikeTitle =
      words.length >= 1 &&
      words.length <= 8 &&
      innerRaw.length <= 64 &&
      !fillerPhraseRe.test(innerRaw) &&
      !greetingTokenRe.test(innerRaw) &&
      !sentenceyRe.test(innerRaw);

    if (headingLine) {
      if (looksLikeTitle) {
        transformed.push(`${level} ${cleanHeadingText(innerRaw)}`);
      } else {
        transformed.push(innerRaw);
      }
      continue;
    }

    if (looksLikeTitle) {
      transformed.push(`## ${cleanHeadingText(innerRaw)}`);
    } else {
      transformed.push(innerRaw);
    }
  }

  out = transformed.join("\n");

  // Final rejoin pass (heading transforms can reintroduce blank lines)
  out = out.replace(/\n{3,}/g, "\n\n");
  out = out.replace(/(your latest)\n\n(logged )/gi, "$1 $2");
  out = out.replace(/([a-z],|latest|logged|your|the|a|an|is|at|of|to|for)\n\n([a-z0-9])/gi, "$1 $2");

  // Clean up excessive blank lines
  out = out.replace(/\n{3,}/g, "\n\n");

  return out.trim();
};
